import { useEffect, useMemo, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, CheckCircle, Trash, ChatCircle, Lock, PaperPlaneRight } from "@phosphor-icons/react";

const EMPTY = { retailer_id: "", title: "", query: "", assigned_to: "" };

const STATUS_STYLE = {
  open:     "bg-amber-100 text-amber-800 border-amber-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed:   "bg-slate-200 text-slate-700 border-slate-300",
};

const fmtDT = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function Support() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).some(r => r === "admin" || r === "superuser");
  const [rows, setRows] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const [detail, setDetail] = useState(null);   // ticket object currently opened
  const [commentText, setCommentText] = useState("");
  const [closeText, setCloseText] = useState("");
  const [showClose, setShowClose] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        client.get("/support"),
        client.get("/retailers"),
      ]);
      setRows(s); setRetailers(r);
    } catch (e) { toast.error(apiError(e)); }
    if (isAdmin) {
      try { const { data: u } = await client.get("/users"); setUsers(u); } catch { /* ignore */ }
    } else {
      try {
        const { data: m } = await client.get("/user-names");
        setUsers(Object.entries(m).map(([mobile, name]) => ({ mobile, name })));
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!s) return true;
      return (r.issue_id || "").toLowerCase().includes(s)
        || (r.title || "").toLowerCase().includes(s)
        || (r.query || "").toLowerCase().includes(s)
        || (r.retailer_name || "").toLowerCase().includes(s)
        || (r.raised_by_name || "").toLowerCase().includes(s)
        || (r.assigned_to_name || "").toLowerCase().includes(s);
    });
  }, [rows, statusFilter, q]);

  const create = async () => {
    if (!form.query.trim()) return toast.error("Query is required");
    try {
      await client.post("/support", form);
      toast.success("Ticket raised"); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await client.get(`/support/${id}`);
      setDetail(data);
      setCommentText(""); setCloseText(""); setShowClose(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    try {
      const { data } = await client.get(`/support/${detail.id}`);
      setDetail(data);
    } catch { /* ignore */ }
  };

  const postComment = async () => {
    if (!commentText.trim() || !detail) return;
    try {
      const { data } = await client.post(`/support/${detail.id}/comments`, { text: commentText.trim() });
      setDetail(data); setCommentText("");
      // Also refresh list to keep comment counts fresh
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doClose = async () => {
    if (!detail) return;
    try {
      const { data } = await client.post(`/support/${detail.id}/close`, { resolution: closeText.trim() });
      setDetail(data); setShowClose(false); setCloseText(""); load();
      toast.success("Ticket closed");
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!confirm("Delete this ticket permanently?")) return;
    try { await client.delete(`/support/${id}`); load(); }
    catch (e2) { toast.error(apiError(e2)); }
  };

  const canClose = (t) => t && t.status !== "closed" && (isAdmin || t.raised_by === user.mobile);
  const canComment = (t) => t && t.status !== "closed";

  return (
    <div>
      <PageHeader title="Support" subtitle="Team tickets · comments · visibility limited to creator, admin, assignee"
        actions={<Button data-testid="support-new-btn" onClick={()=>{setForm(EMPTY);setOpen(true);}} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} className="mr-1"/> Raise Ticket</Button>}
      />
      <Section>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Input data-testid="support-search" placeholder="Search issue, title, party…" value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-xs rounded-sm"/>
          <div className="flex gap-1">
            {["all","open","resolved","closed"].map(s => (
              <button key={s} data-testid={`support-filter-${s}`} onClick={()=>setStatusFilter(s)}
                      className={`text-xs px-3 py-1.5 rounded-sm border font-semibold uppercase tracking-wider ${statusFilter===s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                {s}
              </button>
            ))}
          </div>
          <div className="ml-auto text-xs text-slate-500">{filtered.length} of {rows.length}</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">Issue ID</th>
              <th className="px-4 py-3">Title / Query</th>
              <th className="px-4 py-3">Retailer</th>
              <th className="px-4 py-3">Raised By</th>
              <th className="px-4 py-3">Assigned To</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Comments</th>
              <th className="px-4 py-3">Raised</th>
              <th className="px-4 py-3 w-20">Actions</th>
            </tr></thead>
            <tbody data-testid="support-tbody">
              {filtered.map(s => (
                <tr key={s.id} data-testid={`support-row-${s.id}`}
                    onClick={()=>openDetail(s.id)}
                    className="border-b border-slate-100 hover:bg-slate-50 align-top cursor-pointer">
                  <td className="px-4 py-2 font-mono font-semibold text-slate-900">{s.issue_id || "—"}</td>
                  <td className="px-4 py-2 max-w-sm">
                    {s.title && <div className="font-semibold text-slate-900">{s.title}</div>}
                    <div className="text-slate-600 text-xs line-clamp-2">{s.query}</div>
                  </td>
                  <td className="px-4 py-2 text-slate-700">{s.retailer_name || "—"}</td>
                  <td className="px-4 py-2">{s.raised_by_name || s.raised_by}</td>
                  <td className="px-4 py-2">{s.assigned_to_name || s.assigned_to || "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${STATUS_STYLE[s.status] || STATUS_STYLE.open}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600 text-xs">
                    <span className="inline-flex items-center gap-1"><ChatCircle size={12}/> {(s.comments || []).length}</span>
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{fmtDT(s.query_date)}</td>
                  <td className="px-4 py-2">
                    {isAdmin && (
                      <button data-testid={`support-delete-${s.id}`} onClick={(e)=>remove(s.id, e)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash size={14}/></button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length===0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No tickets found</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Raise Ticket Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Raise Support Ticket</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <label className="label-cap">Retailer (optional)</label>
              <Select value={form.retailer_id} onValueChange={(v)=>setForm({...form, retailer_id: v})}>
                <SelectTrigger data-testid="support-retailer-select" className="rounded-sm"><SelectValue placeholder="Select retailer"/></SelectTrigger>
                <SelectContent>{retailers.map(r=><SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Title</label>
              <Input data-testid="support-title-input" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} placeholder="Short summary"/>
            </div>
            <div>
              <label className="label-cap">Assign To <span className="text-slate-400">(leave blank → auto-assigned to Admin)</span></label>
              <Select value={form.assigned_to} onValueChange={(v)=>setForm({...form, assigned_to: v})}>
                <SelectTrigger data-testid="support-assignee-select" className="rounded-sm"><SelectValue placeholder="Auto-assign to Admin"/></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id || u.mobile} value={u.mobile}>{u.name || u.mobile}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Query / Description</label>
              <Textarea data-testid="support-query-input" value={form.query} onChange={(e)=>setForm({...form, query: e.target.value})} rows={4}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="support-submit-btn" onClick={create} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Raise Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog with comments thread */}
      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="font-mono">{detail?.issue_id || "—"}</span>
              {detail?.status && (
                <span className={`inline-flex px-2 py-0.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${STATUS_STYLE[detail.status] || STATUS_STYLE.open}`}>{detail.status}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              {detail.title && <div className="text-base font-semibold text-slate-900">{detail.title}</div>}
              <div className="text-sm text-slate-700 whitespace-pre-line bg-slate-50 border border-slate-200 rounded-sm p-3">{detail.query}</div>
              <div className="grid grid-cols-2 gap-3 text-xs text-slate-600">
                <div><div className="label-cap">Raised by</div><div className="font-semibold text-slate-800">{detail.raised_by_name || detail.raised_by}</div><div className="text-slate-400">{fmtDT(detail.query_date)}</div></div>
                <div><div className="label-cap">Assigned to</div><div className="font-semibold text-slate-800">{detail.assigned_to_name || detail.assigned_to || "—"}</div></div>
                {detail.retailer_name && <div><div className="label-cap">Retailer</div><div className="font-semibold text-slate-800">{detail.retailer_name}</div></div>}
                {detail.resolution && <div><div className="label-cap">Resolution</div><div className="text-slate-700">{detail.resolution}</div><div className="text-slate-400">by {detail.resolved_by_name}</div></div>}
              </div>

              <div>
                <div className="label-cap mb-2 flex items-center gap-2"><ChatCircle size={12}/> Comments ({(detail.comments || []).length})</div>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1" data-testid="support-comments">
                  {(detail.comments || []).length === 0 && <div className="text-xs text-slate-400 italic">No comments yet</div>}
                  {(detail.comments || []).map((c, i) => (
                    <div key={i} className="text-sm bg-slate-50 border border-slate-200 rounded-sm p-2">
                      <div className="flex justify-between items-baseline text-xs text-slate-500 mb-1">
                        <span className="font-semibold text-slate-800">{c.author_name || c.user}</span>
                        <span>{fmtDT(c.at)}</span>
                      </div>
                      <div className="text-slate-700 whitespace-pre-line">{c.text}</div>
                    </div>
                  ))}
                </div>
              </div>

              {canComment(detail) ? (
                <div>
                  <div className="flex gap-2">
                    <Textarea data-testid="support-comment-input" value={commentText} onChange={(e)=>setCommentText(e.target.value)} rows={2} placeholder="Add a comment…" className="rounded-sm"/>
                    <Button data-testid="support-comment-send" onClick={postComment} className="bg-slate-900 hover:bg-slate-800 rounded-sm self-start" disabled={!commentText.trim()}>
                      <PaperPlaneRight size={14} className="mr-1"/> Post
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500 bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 flex items-center gap-2">
                  <Lock size={12}/> Ticket is closed — comments locked.
                </div>
              )}

              {showClose && (
                <div className="space-y-2 border-t pt-3">
                  <label className="label-cap">Close resolution (optional)</label>
                  <Textarea data-testid="support-close-input" value={closeText} onChange={(e)=>setCloseText(e.target.value)} rows={2} className="rounded-sm"/>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {detail && canClose(detail) && !showClose && (
              <Button data-testid="support-close-btn" onClick={()=>setShowClose(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
                <CheckCircle size={14} className="mr-1"/> Close Ticket
              </Button>
            )}
            {showClose && (
              <>
                <Button variant="outline" onClick={()=>setShowClose(false)} className="rounded-sm">Cancel</Button>
                <Button data-testid="support-close-confirm" onClick={doClose} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Confirm Close</Button>
              </>
            )}
            {!showClose && <Button variant="outline" onClick={()=>setDetail(null)} className="rounded-sm">Done</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
